import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useKasa } from '../../context/KasaContext';
import DatePicker from '../../components/DatePicker';
import { GelirEkleModal, GiderEkleModal, ConfirmModal, SearchableSelect } from '../../components/KasaModals';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import EmptyState from '../../components/EmptyState';

function bugunStr() {
  const d = new Date();
  return [String(d.getDate()).padStart(2, '0'), String(d.getMonth() + 1).padStart(2, '0'), d.getFullYear()].join('.');
}
function saatStr() {
  const d = new Date();
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}
function strToDate(s) { if(!s) return new Date(); const p = s.split('.'); return new Date(p[2], p[1] - 1, p[0]); }
function ddmmyyyyToISO(s) { if(!s) return ''; var p = s.split('.'); return p[2]+'-'+p[1]+'-'+p[0]; }

// ── Icons ────────────────────────────────────────────────────────────────
function EyeIcon({ color }) {
  return (
    <div className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:bg-gray-100 active:scale-90" style={{ color: color || '#d1d5db' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}


// ── Local Components ──────────────────────────────────────────────────────


export default function GunlukKasa() {
  const bugun = bugunStr();
  const {
    gelirler, giderler, adisyonlar,
    gelirGruplari, giderGruplari, personeller,
    odemeTipleri, bankalar, paraBirimleri, dovizKurlari,
    yukleniyor, fetchVeriler,
    gelirEkle, gelirGuncelle, gelirSil,
    giderEkle, giderGuncelle, giderSil,
  } = useKasa();

  const [seciliTarih, setSeciliTarih] = useState(bugun);
  const [aktifTarih, setAktifTarih] = useState(bugun);
  const [personelFiltre, setPersonelFiltre] = useState('Hepsi');
  const [odemeTipFiltre, setOdemeTipFiltre] = useState('Hepsi');
  const [bankaFiltre, setBankaFiltre] = useState('Hepsi');
  const [aktifOdemeTip, setAktifOdemeTip] = useState('Hepsi');
  const [aktifPersonel, setAktifPersonel] = useState('Hepsi');
  const [paraBirimi, setParaBirimi] = useState('TL');
  const [activeTab, setActiveTab] = useState('gelir'); // 'gelir', 'adisyon', 'gider'

  const [gelirFiltreler, setGelirFiltreler] = useState({ tarih: '', saat: '', tutar: '', odemeTip: '', aciklama: '', personel: '' });
  const [giderFiltreler, setGiderFiltreler] = useState({ tarih: '', ad: '', tutar: '', odemeTip: '' });
  const [gelirModal, setGelirModal] = useState(false);
  const [giderModal, setGiderModal] = useState(false);
  const [detayGelir, setDetayGelir] = useState(null);
  const [detayGider, setDetayGider] = useState(null);

  useEffect(() => { fetchVeriler(); }, []);

  const paraBirimiListesi = paraBirimleri.length > 0 ? paraBirimleri.map(p => p.tcmbKod || p.ad) : ['TL', 'EURO', 'USD'];
  const odemeTipListesi = odemeTipleri.length > 0 ? odemeTipleri.map(o => o.ad) : ['BANKA HAVALE', 'NAKİT', 'KREDİ KARTI', 'ÇEK'];
  const bankaListesi = bankalar.length > 0 ? bankalar.map(b => b.bankaAdi || b.ad) : [];

  useEffect(() => {
    if (paraBirimiListesi.length > 0 && !paraBirimiListesi.includes(paraBirimi)) {
      setParaBirimi(paraBirimiListesi.find(p => p === 'TRY' || p === 'TL') || paraBirimiListesi[0]);
    }
  }, [paraBirimleri]);

  // kayitKurDegeri: işlem anında kaydedilen kur (geçmiş tarihler için)
  function dovizCevir(tutar, kaynakKod, kayitKurDegeri) {
    if (!tutar || tutar === 0) return 0;
    const kaynak = (kaynakKod === 'TRY' ? 'TL' : (kaynakKod || 'TL'));
    const hedef = (paraBirimi === 'TRY' ? 'TL' : (paraBirimi || 'TL'));

    // Eğer kaynak ve hedef aynıysa, kur farkı hesaplamaya gerek yok (12 USD her zaman 12 USD'dir)
    if (kaynak === hedef) return tutar;

    // Kaynak → TL: geçmişte kaydedilen kuru kullan, yoksa canlı kuru kullan
    const kaynakTlOrani = (kayitKurDegeri && kayitKurDegeri > 0 && kaynak !== 'TL')
      ? kayitKurDegeri
      : (dovizKurlari[kaynak] || 1);

    const tlDeger = tutar * kaynakTlOrani;
    return tlDeger / (dovizKurlari[hedef] || 1);
  }

  function gunStr(tarih, fark) {
    const [g, a, y] = tarih.split('.').map(Number);
    const d = new Date(y, a - 1, g + fark);
    return [String(d.getDate()).padStart(2, '0'), String(d.getMonth() + 1).padStart(2, '0'), d.getFullYear()].join('.');
  }

  function gunGec(fark) {
    const yeni = gunStr(seciliTarih, fark);
    setSeciliTarih(yeni);
    setAktifTarih(yeni);
  }

  function filtrele() {
    setAktifTarih(seciliTarih);
    setAktifOdemeTip(odemeTipFiltre);
    setAktifPersonel(personelFiltre);
  }

  function temizle() {
    setSeciliTarih(bugun); setAktifTarih(bugun);
    setPersonelFiltre('Hepsi'); setOdemeTipFiltre('Hepsi'); setBankaFiltre('Hepsi');
    setAktifOdemeTip('Hepsi'); setAktifPersonel('Hepsi');
  }

  function gunFiltrele(k) {
    const kTarih = strToDate(k.tarih || '01.01.2000');
    const gunBas = strToDate(aktifTarih);
    const gunSon = strToDate(aktifTarih);
    gunSon.setHours(23, 59, 59, 999);
    return kTarih >= gunBas && kTarih <= gunSon;
  }

  const filtreliGelirler = gelirler.filter(k =>
    gunFiltrele(k) &&
    (aktifOdemeTip === 'Hepsi' || k.odemeTip === aktifOdemeTip) &&
    (aktifPersonel === 'Hepsi' || k.personel === aktifPersonel) &&
    (!gelirFiltreler.tarih || k.tarih?.includes(gelirFiltreler.tarih)) &&
    (!gelirFiltreler.tutar || String(k.tutar).includes(gelirFiltreler.tutar)) &&
    (!gelirFiltreler.aciklama || k.ad?.toLocaleLowerCase('tr-TR').includes(gelirFiltreler.aciklama.toLocaleLowerCase('tr-TR')))
  );

  const filtreliGiderler = giderler.filter(k =>
    gunFiltrele(k) &&
    (aktifOdemeTip === 'Hepsi' || k.odemeTip === aktifOdemeTip) &&
    (aktifPersonel === 'Hepsi' || k.personel === aktifPersonel) &&
    (!giderFiltreler.ad || k.ad?.toLocaleLowerCase('tr-TR').includes(giderFiltreler.ad.toLocaleLowerCase('tr-TR'))) &&
    (!giderFiltreler.tutar || String(k.tutar).includes(giderFiltreler.tutar))
  );

  const filtreliAdisyonlar = adisyonlar.filter(gunFiltrele);

  // Adisyon ödemeleri Odeme tablosuna da kaydediliyor; çift saymamak için kart hesabında hariç tut
  const toplamGelir = filtreliGelirler
    .filter(k => !k.isAdisyonOdemesi)
    .reduce((acc, k) => acc + dovizCevir(k.tutar, k.paraBirimi, k.kurDegeri), 0);
  const toplamGider = filtreliGiderler.reduce((acc, k) => acc + dovizCevir(k.toplam || (k.tutar * (k.miktar || 1)), k.paraBirimi, k.kurDegeri), 0);
  const toplamAdisyon = filtreliAdisyonlar.reduce((acc, k) => acc + dovizCevir(k.toplamTutar || 0, 'TL', null), 0);
  const toplamAdisyonOdendi = filtreliAdisyonlar.filter(a => a.odemeyapildiMi).reduce((acc, k) => acc + dovizCevir(k.toplamTutar || 0, 'TL', null), 0);
  const toplamAdisyonBekliyor = filtreliAdisyonlar.filter(a => !a.odemeyapildiMi).reduce((acc, k) => acc + dovizCevir(k.toplamTutar || 0, 'TL', null), 0);
  const toplamKalan = toplamGelir + toplamAdisyonOdendi - toplamGider;
  const TABS = [
    { id: 'gelir', label: 'Gelirler', icon: '💰', color: 'bg-emerald-500' },
    { id: 'adisyon', label: 'Adisyonlar', icon: '🍽️', color: 'bg-orange-500' },
    { id: 'gider', label: 'Giderler', icon: '💸', color: 'bg-rose-500' },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20">
      {/* ── Header ── */}
      <div className="bg-[#0f172a] pt-12 pb-28 px-4 sm:px-8 relative overflow-hidden shadow-[0_20px_50px_rgba(15,23,42,0.3)]">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-[100px] pointer-events-none" />

        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row md:items-center justify-between gap-8 relative z-10">
          <div>
             <div className="flex items-center gap-3 mb-4 opacity-50">
                <span className="text-white text-[10px] font-black tracking-[0.3em] uppercase">Mali Yönetim Merkezi</span>
                <span className="text-white/20">/</span>
                <span className="text-white text-[10px] font-black tracking-[0.3em] uppercase">Günlük Nakit Akışı</span>
             </div>
             <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-white/5 backdrop-blur-2xl rounded-[2.5rem] border border-white/10 flex items-center justify-center text-4xl shadow-2xl relative group overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                   <span className="relative z-10">📅</span>
                </div>
                <div>
                   <h1 className="text-5xl font-black text-white tracking-tighter leading-tight drop-shadow-2xl">Günlük Kasa</h1>
                   <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                         <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
                         <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest leading-none">Canlı Rapor</p>
                      </div>
                      <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em]">{aktifTarih} Verileri</p>
                   </div>
                </div>
             </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
             <div className="bg-white/5 backdrop-blur-2xl rounded-[2.5rem] p-2 border border-white/10 flex items-center shadow-2xl">
                <button onClick={() => gunGec(-1)} className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all font-black text-xl active:scale-90">‹</button>
                <div className="px-3 sm:px-6 flex items-center gap-2 sm:gap-4 border-x border-white/5">
                   <div className="hidden sm:flex w-8 h-8 rounded-xl bg-indigo-500/20 items-center justify-center text-indigo-300">
                      <CalendarIcon />
                   </div>
                   <DatePicker
                      value={seciliTarih}
                      onChange={val => { setSeciliTarih(val); setAktifTarih(val); }}
                      className="bg-transparent border-none text-white font-black text-sm w-28 sm:w-32 focus:ring-0 p-0 text-center cursor-pointer placeholder-white/50"
                   />
                </div>
                <button onClick={() => gunGec(1)} className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all font-black text-xl active:scale-90">›</button>
             </div>
             <button onClick={temizle} className="h-12 sm:h-16 px-6 sm:px-10 bg-indigo-600 hover:bg-indigo-500 text-white border border-indigo-400/30 rounded-[2.5rem] font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-[0_15px_30px_rgba(79,70,229,0.3)] hover:-translate-y-1 active:translate-y-0">Bugüne Dön</button>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 -mt-16 relative z-20 space-y-8">
        
        {/* Filter Bar */}
        <div className="bg-white/90 backdrop-blur-2xl rounded-[3rem] p-4 border border-white shadow-[0_25px_60px_rgba(0,0,0,0.08)] flex flex-wrap items-center justify-between gap-4 animate-slideDown">
           <div className="flex flex-wrap items-center gap-4 flex-1 px-2 sm:px-4">
              <div className="flex flex-wrap items-center gap-2">
                 <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] opacity-60 whitespace-nowrap">Para Birimi</span>
                 <div className="flex flex-wrap items-center gap-1 bg-gray-50 p-1 rounded-2xl border border-gray-100">
                    {paraBirimiListesi.map(pb => (
                       <button
                          key={pb}
                          onClick={() => setParaBirimi(pb)}
                          className={`px-3 sm:px-5 py-1.5 sm:py-2 rounded-xl text-[10px] font-black transition-all ${paraBirimi === pb ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-200' : 'hover:bg-gray-200 text-gray-400'}`}
                       >
                          {pb}
                       </button>
                    ))}
                 </div>
              </div>

              <div className="h-10 w-px bg-gray-100 hidden lg:block" />

              <div className="flex flex-wrap items-center gap-3">
                 <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] opacity-60 whitespace-nowrap">Ödeme</span>
                    <select value={odemeTipFiltre} onChange={e => setOdemeTipFiltre(e.target.value)} className="h-10 px-3 bg-gray-50 hover:bg-white border border-gray-100 rounded-2xl text-[10px] font-black uppercase tracking-widest cursor-pointer transition-all outline-none min-w-[120px] focus:ring-4 focus:ring-indigo-50">
                       {['Hepsi', ...odemeTipListesi].map(o => <option key={o}>{o}</option>)}
                    </select>
                 </div>

                 <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] opacity-60 whitespace-nowrap">Personel</span>
                    <select value={personelFiltre} onChange={e => setPersonelFiltre(e.target.value)} className="h-10 px-3 bg-gray-50 hover:bg-white border border-gray-100 rounded-2xl text-[10px] font-black uppercase tracking-widest cursor-pointer transition-all outline-none min-w-[130px] focus:ring-4 focus:ring-indigo-50">
                       {['Hepsi', ...personeller.map(p => p.adSoyad)].map(p => <option key={p}>{p}</option>)}
                    </select>
                 </div>

                 <button onClick={filtrele} className="h-10 px-6 bg-gray-900 hover:bg-black text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-xl active:scale-95">Yenile</button>
              </div>
           </div>

           <div className="flex items-center gap-3 px-4">
              <button onClick={() => {
                var tarihStr = aktifTarih;
                if (activeTab === 'gelir') {
                  downloadExcel('gunluk-kasa-gelir-' + tarihStr, 'Gelirler',
                    ['Tarih','Saat','Kaynak','Kategori','Açıklama','Ödeme Tipi','Tutar','Para Birimi'],
                    filtreliGelirler.map(k => [k.tarih, k.saat, k.kaynak, k.grup||'Genel', k.ad, k.odemeTip, k.tutar, k.paraBirimi]));
                } else if (activeTab === 'adisyon') {
                  downloadExcel('gunluk-kasa-adisyon-' + tarihStr, 'Adisyonlar',
                    ['Tarih','Rez. No','Hizmet/Ürün','Miktar','Birim Fiyat','Toplam','Durum'],
                    filtreliAdisyonlar.map(a => [a.tarih, '#'+a.rezervasyonId, a.stokAdi, a.miktar, (a.birimFiyat||0).toFixed(2)+' TL', (a.toplamTutar||0).toFixed(2)+' TL', a.odemeyapildiMi ? 'Ödendi' : 'Bekliyor']));
                } else {
                  downloadExcel('gunluk-kasa-gider-' + tarihStr, 'Giderler',
                    ['Tarih','Kategori','Açıklama','Ödeme Tipi','Personel','Tutar','Para Birimi'],
                    filtreliGiderler.map(k => [k.tarih+' '+(k.saat||''), k.altTip==='personel'?'Personel Gideri':(k.grup||'Genel Gider'), k.ad, k.odemeTip, k.personel||'—', (k.toplam||(k.tutar*(k.miktar||1))).toFixed(2), k.paraBirimi]));
                }
              }} className="h-12 px-6 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all">Excel</button>
              <button onClick={() => {
                var tarihStr = aktifTarih;
                if (activeTab === 'gelir') {
                  printTable('Günlük Kasa — Gelirler',
                    ['Tarih','Saat','Kaynak','Kategori','Açıklama','Ödeme Tipi','Tutar','PB'],
                    filtreliGelirler.map(k => [k.tarih, k.saat, k.kaynak, k.grup||'Genel', k.ad, k.odemeTip, k.tutar, k.paraBirimi]),
                    tarihStr + ' · ' + filtreliGelirler.length + ' kayıt');
                } else if (activeTab === 'adisyon') {
                  printTable('Günlük Kasa — Adisyonlar',
                    ['Tarih','Rez. No','Hizmet/Ürün','Miktar','Birim Fiyat','Toplam','Durum'],
                    filtreliAdisyonlar.map(a => [a.tarih, '#'+a.rezervasyonId, a.stokAdi, a.miktar, (a.birimFiyat||0).toFixed(2)+' TL', (a.toplamTutar||0).toFixed(2)+' TL', a.odemeyapildiMi ? 'Ödendi' : 'Bekliyor']),
                    tarihStr + ' · ' + filtreliAdisyonlar.length + ' kayıt');
                } else {
                  printTable('Günlük Kasa — Giderler',
                    ['Tarih','Kategori','Açıklama','Ödeme Tipi','Personel','Tutar','PB'],
                    filtreliGiderler.map(k => [k.tarih+' '+(k.saat||''), k.altTip==='personel'?'Personel Gideri':(k.grup||'Genel Gider'), k.ad, k.odemeTip, k.personel||'—', (k.toplam||(k.tutar*(k.miktar||1))).toFixed(2), k.paraBirimi]),
                    tarihStr + ' · ' + filtreliGiderler.length + ' kayıt');
                }
              }} className="h-12 px-6 bg-orange-50 text-orange-600 border border-orange-100 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-orange-600 hover:text-white transition-all">Yazdır</button>
           </div>
        </div>


        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {[
            { label: '📝 REZERVASYON (NAKİT/BANKA)', val: toplamGelir, color: 'text-indigo-600', bg: 'bg-indigo-50', icon: '💰' },
            { 
               label: '🍽 ADİSYONLAR', 
               val: toplamAdisyonOdendi, 
               color: 'text-orange-600', 
               bg: 'bg-orange-50', 
               icon: '🍽️',
               sub: toplamAdisyonBekliyor > 0 ? `${toplamAdisyonBekliyor.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${paraBirimi} Beklemede` : null
            },
            { label: '📈 GÜNLÜK TOPLAM GELİR', val: toplamGelir + toplamAdisyonOdendi, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: '📈' },
            { label: '📉 GÜNLÜK TOPLAM GİDER', val: toplamGider, color: 'text-rose-600', bg: 'bg-rose-50', icon: '📉' },
            { 
               label: '🏦 NET KASA', 
               val: toplamKalan, 
               color: toplamKalan >= 0 ? 'text-indigo-900' : 'text-rose-900', 
               bg: toplamKalan >= 0 ? 'bg-indigo-900/5' : 'bg-rose-900/5', 
               icon: '🏦',
               highlight: true
            },
          ].map(k => (
            <div key={k.label} className={`rounded-[2.5rem] p-8 border border-white shadow-xl transition-all hover:translate-y-[-4px] hover:shadow-2xl relative overflow-hidden group ${k.bg}`}>
               <div className="absolute top-0 right-0 p-6 text-4xl opacity-10 transition-all group-hover:scale-125 group-hover:rotate-12">{k.icon}</div>
               <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-3 leading-relaxed">{k.label}</p>
               <h3 className={`text-2xl font-black tracking-tighter ${k.color}`}>
                  {k.val.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-xs opacity-50">{paraBirimi}</span>
               </h3>
               {k.sub && <p className="text-[9px] font-black text-orange-500/70 border-t border-orange-200/30 pt-3 mt-3 animate-pulse">{k.sub}</p>}
               {k.highlight && <div className="absolute bottom-0 left-0 h-1.5 w-full bg-current opacity-20" />}
            </div>
          ))}
        </div>

        {/* Tab Selection */}
        <div className="flex items-center gap-1 sm:gap-4 bg-white/40 p-2 rounded-[2.5rem] border border-white max-w-2xl mx-auto shadow-sm">
           {TABS.map(t => (
              <button
                 key={t.id}
                 onClick={() => setActiveTab(t.id)}
                 className={`flex-1 h-12 sm:h-14 rounded-[2rem] flex items-center justify-center gap-1 sm:gap-3 transition-all duration-500 ${activeTab === t.id ? 'bg-white shadow-xl text-indigo-900 scale-105 border-white border-2' : 'text-gray-400 hover:text-gray-600'}`}
              >
                 <span className="text-base sm:text-lg">{t.icon}</span>
                 <span className="text-[10px] sm:text-xs font-black uppercase tracking-tight sm:tracking-widest">{t.label}</span>
                 {activeTab === t.id && <div className={`hidden sm:block w-2 h-2 rounded-full ${t.color} animate-ping`} />}
              </button>
           ))}
        </div>

        {/* List Content */}
        <div className="animate-fadeIn">
           {activeTab === 'gelir' && (
              <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-white">
                 <div className="bg-gradient-to-r from-emerald-500 to-teal-700 px-4 sm:px-8 py-4 sm:py-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                       <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-2xl flex items-center justify-center text-xl sm:text-2xl shadow-inner shadow-black/5 flex-shrink-0">💰</div>
                       <div>
                          <h2 className="text-base sm:text-xl font-black text-white tracking-tighter uppercase leading-none">Günlük Gelir Listesi</h2>
                          <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1">{filtreliGelirler.length} Hareket Kayıtlı</p>
                       </div>
                    </div>
                    <button onClick={() => setGelirModal(true)} className="h-10 sm:h-12 px-4 sm:px-8 bg-white text-emerald-700 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl flex-shrink-0">Yeni Gelir Ekle</button>
                 </div>
                 
                 <div className="overflow-x-auto">
                    <table className="w-full">
                       <thead>
                          <tr className="bg-gray-50/50 border-b border-gray-100">
                             {['Tarih', 'Saat', 'Kaynak', 'Kategori', 'Açıklama', 'Ödeme Tipi', 'Tutar', 'İşlem'].map(h => (
                                <th key={h} className="text-left py-6 px-6 font-black text-gray-400 text-[10px] uppercase tracking-widest">{h}</th>
                             ))}
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-gray-50">
                          {filtreliGelirler.length === 0 ? (
                             <EmptyState as="td" colSpan={8} text="Henüz Bir Gelir Kaydı Bulunmuyor" />
                          ) : filtreliGelirler.map(k => (
                             <tr key={k.id} className="hover:bg-gray-50 transition-colors group">
                                <td className="py-5 px-6 text-[11px] font-black text-gray-400">{k.tarih}</td>
                                <td className="py-5 px-6 text-[11px] font-black text-gray-400">{k.saat}</td>
                                <td className="py-5 px-6">
                                   <div className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase text-center w-max ${
                                      k.kaynak === 'rezervasyon' ? 'bg-indigo-50 text-indigo-600' :
                                      k.kaynak === 'walk-in' ? 'bg-orange-50 text-orange-600' :
                                      k.kaynak === 'havuz' ? 'bg-cyan-50 text-cyan-600' : 'bg-emerald-50 text-emerald-600'
                                   }`}>
                                      {k.kaynak}
                                   </div>
                                </td>
                                <td className="py-5 px-6 text-xs font-black text-gray-900 uppercase tracking-tighter opacity-60">{k.grup || 'Genel'}</td>
                                <td className="py-5 px-6">
                                   <p className="text-xs font-black text-gray-800 tracking-tighter truncate max-w-[240px]">{k.ad}</p>
                                   <p className="text-[9px] font-black text-gray-400 uppercase mt-0.5 tracking-widest">{k.personel || 'Sistem'}</p>
                                </td>
                                <td className="py-5 px-6">
                                   <span className="text-[10px] font-black text-gray-500 bg-gray-100 px-3 py-1 rounded-lg uppercase">{k.odemeTip}</span>
                                </td>
                                <td className="py-5 px-6">
                                   <p className="text-base font-black text-emerald-600 tracking-tighter">
                                      {k.tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-[10px] opacity-70 uppercase font-black">{k.paraBirimi}</span>
                                   </p>
                                </td>
                                <td className="py-5 px-6">
                                   {k.kaynak === 'kasa' ? (
                                      <button onClick={() => setDetayGelir(k)} className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all shadow-sm active:scale-90">
                                         <EyeIcon color="inherit" />
                                      </button>
                                   ) : (
                                      <div className="w-10 h-10 bg-gray-50 text-gray-300 rounded-xl flex items-center justify-center cursor-not-allowed">
                                         <EyeIcon color="inherit" />
                                      </div>
                                   )}
                                </td>
                             </tr>
                          ))}
                       </tbody>
                    </table>
                 </div>
              </div>
           )}

           {activeTab === 'adisyon' && (
              <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-white animate-fadeIn">
                 <div className="bg-gradient-to-r from-orange-500 to-amber-700 px-8 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                       <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl shadow-inner shadow-black/5">🍽️</div>
                       <div>
                          <h2 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Adisyon Hareketleri</h2>
                          <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">{filtreliAdisyonlar.length} Adisyon Kaydı</p>
                       </div>
                    </div>
                 </div>
                 
                 <div className="overflow-x-auto">
                    <table className="w-full">
                       <thead>
                          <tr className="bg-gray-50/50 border-b border-gray-100">
                             {['Tarih', 'Rez. No', 'Hizmet/Ürün', 'Miktar', 'Birim Fiyat', 'Toplam', 'Durum'].map(h => (
                                <th key={h} className="text-left py-6 px-6 font-black text-gray-400 text-[10px] uppercase tracking-widest">{h}</th>
                             ))}
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-gray-50">
                          {filtreliAdisyonlar.length === 0 ? (
                             <EmptyState as="td" colSpan={7} text="Bu Tarihte Adisyon Bulunmuyor" />
                          ) : filtreliAdisyonlar.map(a => (
                             <tr key={a.id} className="hover:bg-gray-50 transition-colors group text-sm">
                                <td className="py-5 px-6 text-[11px] font-black text-gray-400">{a.tarih}</td>
                                <td className="py-5 px-6">
                                   <span className="bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded-xl text-[10px] font-black">#{a.rezervasyonId}</span>
                                </td>
                                <td className="py-5 px-6 font-black text-gray-800 tracking-tighter uppercase text-xs">{a.stokAdi}</td>
                                <td className="py-5 px-6 text-xs font-black text-gray-400 uppercase tracking-widest">{a.miktar} ADET</td>
                                <td className="py-5 px-6 text-[11px] font-black text-gray-500">{(a.birimFiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                                <td className="py-5 px-6 font-black text-orange-600 text-base">
                                   {(a.toplamTutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-[10px] opacity-70">TL</span>
                                </td>
                                <td className="py-5 px-6">
                                   {a.odemeyapildiMi ? (
                                      <span className="bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm">Ödendi</span>
                                   ) : (
                                      <div className="flex items-center gap-2">
                                         <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shadow-[0_0_8px_rgba(245,158,11,0.5)]" />
                                         <span className="text-amber-700 text-[10px] font-black uppercase tracking-widest">Bekliyor</span>
                                      </div>
                                   )}
                                </td>
                             </tr>
                          ))}
                       </tbody>
                    </table>
                 </div>
              </div>
           )}

           {activeTab === 'gider' && (
              <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-white animate-fadeIn">
                 <div className="bg-gradient-to-r from-rose-500 to-pink-700 px-4 sm:px-8 py-4 sm:py-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                       <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-2xl flex items-center justify-center text-xl sm:text-2xl shadow-inner shadow-black/5 flex-shrink-0">💸</div>
                       <div>
                          <h2 className="text-base sm:text-xl font-black text-white tracking-tighter uppercase leading-none">Günlük Gider Hareketleri</h2>
                          <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1">{filtreliGiderler.length} Harcama Kaydı</p>
                       </div>
                    </div>
                    <button onClick={() => setGiderModal(true)} className="h-10 sm:h-12 px-4 sm:px-8 bg-white text-rose-700 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl flex-shrink-0">Yeni Gider Ekle</button>
                 </div>
                 
                 <div className="overflow-x-auto">
                    <table className="w-full">
                       <thead>
                          <tr className="bg-gray-50/50 border-b border-gray-100">
                             {['Tarih', 'Kategori', 'Açıklama', 'Ödeme Tipi', 'Personel', 'Tutar', 'İşlem'].map(h => (
                                <th key={h} className="text-left py-6 px-6 font-black text-gray-400 text-[10px] uppercase tracking-widest">{h}</th>
                             ))}
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-gray-50">
                          {filtreliGiderler.length === 0 ? (
                             <EmptyState as="td" colSpan={7} text="Harcanacak Bir Şey Bulunamadı" />
                          ) : filtreliGiderler.map(k => (
                             <tr key={k.id} className="hover:bg-gray-50 transition-colors group">
                                <td className="py-5 px-6 text-[11px] font-black text-gray-400">{k.tarih} {k.saat}</td>
                                <td className="py-5 px-6">
                                   <div className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase text-center w-max ${
                                      k.altTip === 'personel' ? 'bg-purple-50 text-purple-600 border border-purple-100' : 'bg-gray-50 text-gray-600 border border-gray-100'
                                   }`}>
                                      {k.altTip === 'personel' ? 'Personel Gideri' : k.grup || 'Genel Gider'}
                                   </div>
                                </td>
                                <td className="py-5 px-6 text-xs font-black text-gray-800 tracking-tighter uppercase truncate max-w-[240px]">{k.ad}</td>
                                <td className="py-5 px-6">
                                   <span className="text-[10px] font-black text-gray-500 bg-gray-100 px-3 py-1 rounded-lg uppercase">{k.odemeTip}</span>
                                </td>
                                <td className="py-5 px-6 text-xs font-black text-gray-400 uppercase tracking-widest">{k.personel || '—'}</td>
                                <td className="py-5 px-6">
                                   <p className="text-base font-black text-rose-600 tracking-tighter">
                                      {(k.toplam || (k.tutar * (k.miktar || 1))).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-[10px] opacity-70 uppercase font-black">{k.paraBirimi}</span>
                                   </p>
                                </td>
                                <td className="py-5 px-6">
                                   <button onClick={() => setDetayGider(k)} className="w-10 h-10 bg-rose-50 text-rose-600 rounded-xl flex items-center justify-center hover:bg-rose-600 hover:text-white transition-all shadow-sm active:scale-90">
                                      <EyeIcon color="inherit" />
                                   </button>
                                </td>
                             </tr>
                          ))}
                       </tbody>
                    </table>
                 </div>
              </div>
           )}
        </div>
      </div>

      {/* ── Modals ── */}
      {gelirModal && (
        <GelirEkleModal
          onClose={() => setGelirModal(false)}
          onKaydet={gelirEkle}
          gelirGruplari={gelirGruplari}
          personeller={personeller}
          odemeTipleri={odemeTipListesi}
          bankalar={bankaListesi}
          paraBirimleri={paraBirimiListesi}
          dovizKurlari={dovizKurlari}
        />
      )}
      {detayGelir && (
        <GelirEkleModal
          mevcutKayit={detayGelir}
          onClose={() => setDetayGelir(null)}
          onKaydet={gelirGuncelle}
          onSil={gelirSil}
          gelirGruplari={gelirGruplari}
          personeller={personeller}
          odemeTipleri={odemeTipListesi}
          bankalar={bankaListesi}
          paraBirimleri={paraBirimiListesi}
          dovizKurlari={dovizKurlari}
        />
      )}
      {giderModal && (
        <GiderEkleModal
          onClose={() => setGiderModal(false)}
          onKaydet={giderEkle}
          giderGruplari={giderGruplari}
          personeller={personeller}
          odemeTipleri={odemeTipListesi}
          bankalar={bankaListesi}
          paraBirimleri={paraBirimiListesi}
          dovizKurlari={dovizKurlari}
        />
      )}
      {detayGider && (
        <GiderEkleModal
          mevcutKayit={detayGider}
          onClose={() => setDetayGider(null)}
          onKaydet={giderGuncelle}
          onSil={giderSil}
          giderGruplari={giderGruplari}
          personeller={personeller}
          odemeTipleri={odemeTipListesi}
          bankalar={bankaListesi}
          paraBirimleri={paraBirimiListesi}
          dovizKurlari={dovizKurlari}
        />
      )}

      {/* ── Alt Bilgi ── */}
      <UsageInfo
        id="kasa-gunluk"
        color="cyan"
        title="Nasıl Kullanılır?"
        description="Günlük kasa sayfasında otelinizin o günkü tüm nakit, kredi kartı ve banka işlemlerini detaylı olarak takip edebilir, yeni gelir veya gider kalemleri ekleyebilirsiniz."
        scenarios={[
          { title: "Yeni Gelir/Gider", content: "Sol üstteki butonlarla saniyeler içinde kasaya giriş veya çıkış yapabilir, bu işlemleri ilgili personellere atayabilirsiniz." },
          { title: "Arama & Filtreleme", content: "Yüzlerce işlem arasında misafir adı veya açıklama ile arama yaparak aradığınız kayda anında ulaşabilirsiniz." },
          { title: "Mali Tablo Kontrolü", content: "Sayfanın en altındaki toplamlar alanından kasanızın o günkü net durumunu ve döviz bazlı dağılımını görebilirsiniz." }
        ]}
      />
    </div>
  );
}
